import React from 'react';
import { Paper, Typography, Box, CircularProgress, Grid } from '@mui/material';
import Chart from 'react-apexcharts';
import { formatCurrency } from '../../../utils/formatCurrency';
import { DASHBOARD_COLORS, sectionPaperSx } from '../dashboardStyles';
import { getSalesOverviewKpiConfig, buildBaseChartOptions } from './salesOverviewConstants';
import SalesOverviewHeader from './SalesOverviewHeader';

const CARD_THEMES = [
  { bg: '#ecfdf5', border: '#a7f3d0', color: '#059669' },
  { bg: '#eff6ff', border: '#bfdbfe', color: '#2563eb' },
  { bg: '#fff7ed', border: '#fed7aa', color: '#ea580c' },
];

/** Template 2 — bar chart with individual colored KPI cards. */
const SalesOverviewTemplate2 = ({
  labels,
  data,
  metrics,
  loading,
  compact = false,
  period = 'monthly',
  onPeriodChange,
  showAiAssistant = false,
  onAiAssistantClick,
}) => {
  const kpiConfig = getSalesOverviewKpiConfig(period);
  const chartHeight = compact ? 110 : 220;
  const chartOptions = {
    ...buildBaseChartOptions(labels),
    colors: ['#7c3aed'],
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: compact ? '55%' : '48%',
      },
    },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
  };

  const series = [{ name: 'Sales', data: data || [] }];

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionPaperSx,
        height: '100%',
        minHeight: compact ? 0 : 320,
        display: 'flex',
        flexDirection: 'column',
        p: compact ? 1.5 : 2.5,
        bgcolor: '#fafbfc',
      }}
    >
      <SalesOverviewHeader
        period={period}
        onPeriodChange={onPeriodChange}
        compact={compact}
        showPeriodToggle={!compact && Boolean(onPeriodChange)}
        showAiAssistant={showAiAssistant}
        onAiAssistantClick={onAiAssistantClick}
      />

      <Grid container spacing={compact ? 0.75 : 1.5} sx={{ mb: compact ? 1 : 2 }}>
        {kpiConfig.map((kpi, index) => {
          const theme = CARD_THEMES[index % CARD_THEMES.length];
          return (
            <Grid item xs={4} key={kpi.key}>
              <Box
                sx={{
                  p: compact ? 0.75 : 1.25,
                  borderRadius: 1.5,
                  bgcolor: theme.bg,
                  border: `1px solid ${theme.border}`,
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: compact ? '0.68rem' : '0.95rem',
                    color: theme.color,
                    lineHeight: 1.2,
                  }}
                >
                  {loading ? '—' : formatCurrency(metrics[kpi.key] ?? 0)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: DASHBOARD_COLORS.textMuted,
                    fontWeight: 600,
                    fontSize: compact ? '0.55rem' : '0.7rem',
                  }}
                >
                  {kpi.label}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          flex: 1,
          minHeight: compact ? 90 : 200,
          bgcolor: DASHBOARD_COLORS.surface,
          borderRadius: 1.5,
          border: `1px solid ${DASHBOARD_COLORS.border}`,
          p: compact ? 0.5 : 1,
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress size={compact ? 24 : 32} />
          </Box>
        ) : (
          <Chart options={chartOptions} series={series} type="bar" height={chartHeight} />
        )}
      </Box>
    </Paper>
  );
};

export default SalesOverviewTemplate2;
